import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type LoginStatus =
  | "ready"
  | "starting"
  | "awaiting_credentials"
  | "awaiting_mfa"
  | "preview_ready"
  | "failed"
  | "expired"
  | "aborted";

type MfaPrompt = {
  kind: "microsoft_authenticator";
  code: string | null;
  detectedAt: number;
};

type PreviewResult = {
  authenticated: boolean;
  detectedPage: string;
  detectedUrl: string | null;
  scrapeTimestamp: string;
  sampleCounts: {
    flights: number | null;
    crewMembers: number | null;
    passengerLoads: number | null;
  };
};

type CredentialAssistStatus = "opening" | "filling" | "submitted" | "needs_manual";

type CredentialAssistState = {
  status: CredentialAssistStatus;
  startedAt?: number;
  updatedAt: number;
  error?: string | null;
};

type Credentials = {
  username: string;
  password: string;
};

type SessionState = {
  id?: string;
  status: LoginStatus;
  ecrewLoginUrl?: string;
  currentUrl?: string | null;
  pageTitle?: string | null;
  liveViewUrl?: string | null;
  liveViewUrlExpiresAt?: number;
  credentialAssist?: CredentialAssistState | null;
  mfaPrompt?: MfaPrompt | null;
  preview?: PreviewResult | null;
  createdAt?: number;
  updatedAt?: number;
  expiresAt?: number;
  message?: string;
  error?: string;
  lastInspectionError?: string;
};

const INITIAL_SESSION: SessionState = {
  status: "ready",
  ecrewLoginUrl: "https://ecrew.transat.com/ecrew/sso/loginoidc",
  message: "Ready to connect to eCrew."
};

const ACTIVE_STATUSES: LoginStatus[] = ["starting", "awaiting_credentials", "awaiting_mfa"];

export function App() {
  const isTsLogbook = useMemo(() => shouldRenderTsLogbook(), []);

  if (!isTsLogbook) {
    return <MainPage />;
  }

  return <TsLogbookApp />;
}

function MainPage() {
  return (
    <main className="blank-canvas" aria-label="wilco.tools blank page">
      <p>you shouldn't be here.</p>
    </main>
  );
}

function TsLogbookApp() {
  const [session, setSession] = useState<SessionState>(INITIAL_SESSION);
  const [isStarting, setIsStarting] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const pollTimer = useRef<number | null>(null);

  const isActive = ACTIVE_STATUSES.includes(session.status);
  const isTerminal = ["failed", "expired", "aborted", "preview_ready"].includes(session.status);

  const refreshStatus = useCallback(async (sessionId: string) => {
    const response = await fetch(`/api/ts-logbook/sessions/${sessionId}/status`, {
      headers: { accept: "application/json" }
    });

    const nextSession = (await response.json()) as SessionState;
    setSession(nextSession);
    setLastCheckedAt(Date.now());
  }, []);

  const startSession = useCallback(async (credentials?: Credentials) => {
    setIsStarting(true);
    setSession({
      ...INITIAL_SESSION,
      status: "starting",
      message: credentials ? "Preparing credential assist..." : "Opening secure sign-in..."
    });

    try {
      const response = await fetch("/api/ts-logbook/sessions", {
        method: "POST",
        headers: credentials
          ? {
              accept: "application/json",
              "content-type": "application/json"
            }
          : { accept: "application/json" },
        body: credentials ? JSON.stringify({ credentials }) : undefined
      });
      const nextSession = (await response.json()) as SessionState;
      setSession(nextSession);
      setLastCheckedAt(Date.now());
    } catch (error) {
      setSession({
        ...INITIAL_SESSION,
        status: "failed",
        error: error instanceof Error ? error.message : String(error),
        message: "Could not open sign-in."
      });
    } finally {
      setIsStarting(false);
    }
  }, []);

  const abortSession = useCallback(async () => {
    if (!session.id) {
      setSession(INITIAL_SESSION);
      return;
    }

    setIsAborting(true);

    try {
      const response = await fetch(`/api/ts-logbook/sessions/${session.id}/abort`, {
        method: "POST",
        headers: { accept: "application/json" }
      });
      setSession((await response.json()) as SessionState);
      setLastCheckedAt(Date.now());
    } catch (error) {
      setSession((current) => ({
        ...current,
        status: "failed",
        error: error instanceof Error ? error.message : String(error),
        message: "Could not close sign-in."
      }));
    } finally {
      setIsAborting(false);
    }
  }, [session.id]);

  const resetSession = useCallback(() => {
    setSession(INITIAL_SESSION);
    setLastCheckedAt(null);
  }, []);

  useEffect(() => {
    if (pollTimer.current) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }

    if (!session.id || !isActive) {
      return;
    }

    pollTimer.current = window.setInterval(() => {
      void refreshStatus(session.id as string);
    }, 4000);

    return () => {
      if (pollTimer.current) {
        window.clearInterval(pollTimer.current);
      }
    };
  }, [isActive, refreshStatus, session.id]);

  return (
    <main className="ts-shell">
      <section className="ts-workspace" aria-label="TS Logbook eCrew sign-in">
        <header className="ts-header">
          <div>
            <p className="ts-app-name">ts-logbook</p>
            <h1>Import your eCrew logbook</h1>
          </div>
          <StatusPill status={session.status} />
        </header>

        <section className="ts-card">
          <SessionStage
            isAborting={isAborting}
            isStarting={isStarting}
            onAbort={abortSession}
            onReset={resetSession}
            onStart={startSession}
            session={session}
          />
        </section>

        <FooterStatus lastCheckedAt={lastCheckedAt} session={session} />
        {isTerminal ? <PreviewBlock preview={session.preview} status={session.status} /> : null}
      </section>
    </main>
  );
}

function SessionStage({
  isAborting,
  isStarting,
  onAbort,
  onReset,
  onStart,
  session
}: {
  isAborting: boolean;
  isStarting: boolean;
  onAbort: () => void;
  onReset: () => void;
  onStart: (credentials?: Credentials) => void;
  session: SessionState;
}) {
  if (session.status === "preview_ready") {
    return (
      <div className="ts-centered-stage">
        <span className="ts-success-mark">Connected</span>
        <h2>eCrew sign-in complete</h2>
        <p>The preview connection is ready for the next scraper pass.</p>
        <button className="ts-button ts-button-primary" type="button" onClick={onReset}>
          Start over
        </button>
      </div>
    );
  }

  if (session.status === "failed" || session.status === "expired" || session.status === "aborted") {
    return (
      <div className="ts-centered-stage">
        <span className="ts-error-mark">{statusLabel(session.status)}</span>
        <h2>Sign-in was not completed</h2>
        <p>{session.error || session.message || "The secure sign-in session ended."}</p>
        <button className="ts-button ts-button-primary" type="button" onClick={() => onStart()} disabled={isStarting}>
          {isStarting ? "Opening..." : "Try again"}
        </button>
      </div>
    );
  }

  if (session.status === "ready") {
    return (
      <div className="ts-centered-stage">
        <span className="ts-soft-mark">Secure session</span>
        <h2>Sign in to eCrew</h2>
        <p>Your login is sent once to an isolated eCrew session and discarded.</p>
        <CredentialAssistForm isStarting={isStarting} onManualStart={() => onStart()} onSubmit={onStart} />
      </div>
    );
  }

  if (session.status === "starting" || !session.liveViewUrl) {
    return (
      <div className="ts-centered-stage">
        <span className="ts-spinner" aria-hidden="true" />
        <h2>Opening sign-in</h2>
        <p>Preparing the secure eCrew session.</p>
        <button className="ts-button" type="button" onClick={onAbort} disabled={isAborting}>
          Cancel
        </button>
      </div>
    );
  }

  if (session.credentialAssist) {
    return (
      <CredentialAssistProgress
        isAborting={isAborting}
        onAbort={onAbort}
        session={session}
      />
    );
  }

  return (
    <div className="ts-signin-stage">
      <div className="ts-signin-topbar">
        <div>
          <h2>{session.status === "awaiting_mfa" ? "Approve sign-in" : "Sign in to eCrew"}</h2>
          <p>{session.status === "awaiting_mfa" ? "Use your authenticator app to continue." : "Complete the prompt below."}</p>
        </div>
        <button className="ts-button" type="button" onClick={onAbort} disabled={isAborting}>
          {isAborting ? "Closing..." : "Cancel"}
        </button>
      </div>

      <MfaBlock prompt={session.mfaPrompt} />

      <div className="ts-frame-shell">
        <iframe
          className="ts-signin-frame"
          referrerPolicy="no-referrer"
          src={session.liveViewUrl}
          title="Secure eCrew sign-in"
        />
      </div>

      <div className="ts-frame-fallback">
        <a href={session.liveViewUrl} target="_blank" rel="noreferrer">
          Open sign-in in a separate window
        </a>
      </div>
    </div>
  );
}

function CredentialAssistForm({
  isStarting,
  onManualStart,
  onSubmit
}: {
  isStarting: boolean;
  onManualStart: () => void;
  onSubmit: (credentials: Credentials) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const canSubmit = username.trim().length > 0 && password.length > 0 && !isStarting;

  return (
    <form
      className="ts-login-form"
      onSubmit={(event) => {
        event.preventDefault();

        if (!canSubmit) {
          return;
        }

        onSubmit({
          username: username.trim(),
          password
        });
      }}
    >
      <label className="ts-field">
        <span>Username</span>
        <input
          autoCapitalize="none"
          autoComplete="username"
          className="ts-input"
          inputMode="email"
          name="username"
          onChange={(event) => setUsername(event.currentTarget.value)}
          placeholder="name@company.com"
          spellCheck={false}
          type="text"
          value={username}
        />
      </label>

      <label className="ts-field">
        <span>Password</span>
        <input
          autoComplete="current-password"
          className="ts-input"
          name="password"
          onChange={(event) => setPassword(event.currentTarget.value)}
          type="password"
          value={password}
        />
      </label>

      <div className="ts-form-actions">
        <button className="ts-button ts-button-primary ts-button-large" type="submit" disabled={!canSubmit}>
          {isStarting ? "Signing in..." : "Sign in and continue"}
        </button>
        <button className="ts-button ts-button-link" type="button" onClick={onManualStart} disabled={isStarting}>
          Use secure window instead
        </button>
      </div>
    </form>
  );
}

function CredentialAssistProgress({
  isAborting,
  onAbort,
  session
}: {
  isAborting: boolean;
  onAbort: () => void;
  session: SessionState;
}) {
  const needsManual = session.credentialAssist?.status === "needs_manual";
  const isMfa = session.status === "awaiting_mfa";
  const heading = needsManual ? "Continue sign-in" : isMfa ? "Approve sign-in" : "Finishing sign-in";

  return (
    <div className="ts-assist-progress">
      <div className="ts-signin-topbar">
        <div>
          <h2>{heading}</h2>
          <p>
            {needsManual
              ? "Microsoft needs one more step in the secure sign-in window."
              : isMfa
                ? "Use your authenticator app to approve the request."
                : "Waiting for Microsoft to show the next step."}
          </p>
        </div>
        <button className="ts-button" type="button" onClick={onAbort} disabled={isAborting}>
          {isAborting ? "Closing..." : "Cancel"}
        </button>
      </div>

      <MfaBlock prompt={session.mfaPrompt} />

      <div className="ts-assist-panel">
        <span className="ts-spinner" aria-hidden="true" />
        <div>
          <h3>{needsManual ? "Manual step required" : "Credentials submitted"}</h3>
          <p>
            {needsManual
              ? session.credentialAssist?.error || "The automatic sign-in could not finish."
              : "Your password is not stored. The isolated session closes when this import ends."}
          </p>
        </div>
      </div>

      <div className="ts-assist-actions">
        {session.liveViewUrl ? (
          <a className="ts-button" href={session.liveViewUrl} target="_blank" rel="noreferrer">
            Open secure window
          </a>
        ) : null}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: LoginStatus }) {
  return <span className={`ts-status ts-status-${status}`}>{statusLabel(status)}</span>;
}

function MfaBlock({ prompt }: { prompt?: MfaPrompt | null }) {
  if (!prompt) {
    return null;
  }

  return (
    <div className="ts-mfa">
      <div>
        <h3>Authenticator code</h3>
        <p>Enter the number shown here on your phone.</p>
      </div>
      {prompt.code ? <strong>{prompt.code}</strong> : <span>Waiting</span>}
    </div>
  );
}

function FooterStatus({ lastCheckedAt, session }: { lastCheckedAt: number | null; session: SessionState }) {
  if (session.status === "ready") {
    return null;
  }

  return (
    <div className="ts-footer-status">
      <span>{statusMessage(session)}</span>
      <span>{lastCheckedAt ? `Updated ${formatTime(lastCheckedAt)}` : "Connecting"}</span>
    </div>
  );
}

function PreviewBlock({ preview, status }: { preview?: PreviewResult | null; status: LoginStatus }) {
  if (status !== "preview_ready" || !preview) {
    return null;
  }

  return (
    <section className="ts-result">
      <h2>Preview ready</h2>
      <p>{preview.detectedPage} was detected. Flight extraction is still disabled for this preview version.</p>
    </section>
  );
}

function shouldRenderTsLogbook() {
  const host = window.location.hostname.toLowerCase();
  const params = new URLSearchParams(window.location.search);

  return (
    host === "ts-logbook.wilco.tools" ||
    host.startsWith("ts-logbook.") ||
    window.location.pathname.startsWith("/ts-logbook") ||
    params.get("tool") === "ts-logbook"
  );
}

function statusLabel(status: LoginStatus) {
  switch (status) {
    case "ready":
      return "Ready";
    case "starting":
      return "Opening";
    case "awaiting_credentials":
      return "Sign in";
    case "awaiting_mfa":
      return "Authenticator";
    case "preview_ready":
      return "Connected";
    case "failed":
      return "Failed";
    case "expired":
      return "Expired";
    case "aborted":
      return "Cancelled";
  }
}

function statusMessage(session: SessionState) {
  switch (session.status) {
    case "starting":
      return "Preparing the secure eCrew session.";
    case "awaiting_credentials":
      if (session.credentialAssist?.status === "submitted") {
        return "Credentials submitted. Waiting for Microsoft.";
      }

      if (session.credentialAssist?.status === "needs_manual") {
        return "Manual sign-in step required.";
      }

      if (session.credentialAssist) {
        return "Securely filling the eCrew/Microsoft sign-in.";
      }

      return "Complete the secure eCrew/Microsoft sign-in below.";
    case "awaiting_mfa":
      return "Approve the sign-in in your authenticator app.";
    case "preview_ready":
      return "eCrew sign-in is complete.";
    case "aborted":
      return "The sign-in session was cancelled.";
    case "expired":
      return "The sign-in session expired.";
    case "failed":
      return session.message || "Could not complete sign-in.";
    case "ready":
      return INITIAL_SESSION.message;
  }
}

function formatTime(value: number) {
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}
